import { FC } from 'react'

import {
    Avatar,
    Box,
    Button,
    Flex,
    IconButton,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverFooter,
    PopoverHeader,
    PopoverTrigger,
    Text,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { useLocalStorage } from 'usehooks-ts'

import { useLogoutMutation } from '@core/app/api/authApi'
import { setResetForm } from '@core/app/slices/profile'
import { storeProfilePicture } from '@core/app/slices/v2/newProfile'
import { MainButton } from '@core/components/MainButton'
import { MenuBurgerIcon } from '@core/components/Sidebar/Icons'
import { useFeedbackModal } from '@core/utils/contexts/FeedbackModalContext'
import { removeLocalStorageItem } from '@core/utils/helpers/localStorageHelpers'
import { useAppDispatch, useAppSelector } from '@core/utils/hooks/hook'
import useLocalStorageStore from '@core/utils/hooks/useLocalStorageStore'

import { IconFeedback } from '@page/absence/assets/icons'

interface Props {
    isFixed?: boolean
    title?: string
}


export const EmployeeHeader: FC<Props> = ({ isFixed = 'false', title }): JSX.Element => {
    const dispatch = useAppDispatch()
    const [logout] = useLogoutMutation()
    const { open } = useFeedbackModal()
    const profileAvatar = useAppSelector(storeProfilePicture)
    const [value, setValue] = useLocalStorage('isSidebarOpened', true)
    const [, , removeServiceCode] = useLocalStorageStore('service_code')
    // const [popoverOpened, setPopoverOpened] = useState(false)
    const employeeProfile = window.employeeData?.profile
    const nameLabel = [employeeProfile?.firstName, employeeProfile?.lastName].filter(Boolean).join(' ')
    const handleToggle = () => {
        const newValue = !value
        setValue(newValue)
        window.dispatchEvent(
            new CustomEvent('localStorageChange', {
                detail: { key: 'isSidebarOpened', value: newValue },
            })
        )
    }
    const { t } = useTranslation()

    const handleLogout = async () => {
        try {
            const response = await logout().unwrap()
            removeLocalStorageItem('feedbackTop')
            localStorage.removeItem('mobile_verification')

            location.href = response.redirectUrl
            localStorage.removeItem('apply_dialog')
            removeServiceCode()
            // localStorage.removeItem('service_code')

            localStorage.removeItem('is_logged')
            localStorage.removeItem('after_apply')
            localStorage.removeItem('applying_process')
            localStorage.removeItem('after_apply_id')
            localStorage.removeItem('after_apply_title')
            dispatch(setResetForm(true))
        } catch (error) {}
    }
    return (
        <Box
            w={'full'}
            h={'56px'}
            bg={'#FFFFFF'}
            px={'12px'}
            py={'14px'}
            borderBottom={'1px solid'}
            borderColor={'#EAEAEB'}
            pos={isFixed ? 'fixed' : 'sticky'}
            top={0}
            left={'0'}
            flexShrink={0}
            zIndex={1004}
        >
            <Flex alignItems={'center'} w={'full'} justifyContent={'space-between'} position={'relative'}>
                <IconButton
                    color={'icons.primary'}
                    w={'32px'}
                    minW={'32px'}
                    h={'32px'}
                    background={'none'}
                    _hover={{
                        background: '#F5F5F5',
                        color: 'icons.main',
                    }}
                    borderRadius={'6px'}
                    icon={<MenuBurgerIcon boxSize={'20px'} color={'#4b4B4B'} />}
                    aria-label={'collapse sidebar'}
                    onClick={handleToggle}
                />
                {title && (
                    <Text
                        position={'absolute'}
                        left={'50%'}
                        transform={'translateX(-50%)'}
                        color={'#0C0C0D'}
                        fontWeight={'semibold'}
                        fontSize={'md'}
                        pointerEvents={'none'}
                    >
                        {title}
                    </Text>
                )}
                <Popover
                    // isOpen={popoverOpened}
                    // onOpen={() => setPopoverOpened(true)}
                    // onClose={() => setPopoverOpened(false)}
                    trigger={'hover'}
                >
                    <PopoverTrigger>
                        <Button minW={'32px'} h={'32px'} borderRadius={'full'} p={0} variant={'unstyled'} size={'lg'}>
                            <Avatar w={'32px'} h={'32px'} src={profileAvatar || employeeProfile?.picture} />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent w={'296px'} maxW={'296px'} boxShadow={'0px 8px 24px 0px #9DA0A429'} mr={'12px'}>
                        <PopoverHeader borderBottom={'none'}>
                            <Flex
                                flexDirection={'column'}
                                gap={'12px'}
                                alignItems={'center'}
                                p={'16px'}
                                w={'full'}
                                h={'full'}
                            >
                                <Avatar w={'80px'} h={'80px'} src={profileAvatar || employeeProfile?.picture} />
                                <Flex
                                    color={'#0C0C0D'}
                                    flexDirection={'column'}
                                    alignItems={'center'}
                                    justifyContent={'center'}
                                    w={'full'}
                                >
                                    <Text
                                        fontWeight={'semibold'}
                                        lineHeight={'24px'}
                                        w={'full'}
                                        textAlign={'center'}
                                        noOfLines={2}
                                    >
                                        {nameLabel}
                                    </Text>
                                    <Text fontSize={'sm'} fontWeight={'medium'} whiteSpace={'wrap'} wordBreak={'break-all'}>
                                        {employeeProfile?.title}
                                    </Text>
                                </Flex>
                            </Flex>
                        </PopoverHeader>
                        <PopoverBody px={'8px'} py={0} borderBottom={'none'}>
                            <Flex
                                borderTop={'1px solid #EAEAEB'}
                                borderBottom={'1px solid #EAEAEB'}
                                flexDirection={'column'}
                                justifyContent={'flex-start'}
                                alignItems={'flex-start'}
                                gap={'6px'}
                                py={'12px'}
                            >
                                <MainButton
                                    w={'full'}
                                    view={'ghost'}
                                    size={'md'}
                                    title={t('4647') || 'Profile'}
                                    justifyContent={'flex-start'}
                                    leftIcon={<i className="fa-light fa-user fa-md" />}
                                    _hover={{ bg: '#F5F5F5' }}
                                    transition={'all .3s ease'}
                                    pl={'21px'}
                                    onClick={() => (location.href = '/employee')}
                                />
                                {/* <MainButton
                                    w={'full'}
                                    view={'ghost'}
                                    size={'md'}
                                    title={t('420') || 'Settings'}
                                    justifyContent={'flex-start'}
                                    leftIcon={<i className="fa-light fa-gear fa-md" />}
                                    _hover={{ bg: '#F5F5F5' }}
                                    transition={'all .3s ease'}
                                    onClick={() => (location.href = '/profile/settings')}
                                /> */}
                                <MainButton
                                    w={'full'}
                                    view={'ghost'}
                                    size={'md'}
                                    title={t('9477', 'Submit feedback') || 'Submit feedback'}
                                    justifyContent={'flex-start'}
                                    leftIcon={<IconFeedback color={'#58585B'} />}
                                    _hover={{ bg: '#F5F5F5' }}
                                    transition={'all .3s ease'}
                                    onClick={() => open()}
                                />
                            </Flex>
                        </PopoverBody>
                        <PopoverFooter px={'8px'} py={'12px'} border={'none'}>
                            <MainButton
                                w={'full'}
                                view={'ghost'}
                                size={'md'}
                                title={t('6639') || 'Log out'}
                                justifyContent={'flex-start'}
                                leftIcon={<i className="fa-light fa-arrow-right-from-bracket fa-md" />}
                                _hover={{ bg: '#FCEAEA', color: '#B62626' }}
                                transition={'all .3s ease'}
                                onClick={handleLogout}
                            />
                        </PopoverFooter>
                    </PopoverContent>
                </Popover>
            </Flex>
        </Box>
    )
}
